import React, { type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Search, ChefHat } from 'lucide-react';
import { useCartStore } from '../store/cartStore';
import { resolveAssetUrl } from '../lib/assets';
import type { StoreThemePreset } from '../lib/storeBranding';

interface StoreLayoutProps {
    children: React.ReactNode;
    slug: string;
    establishmentName?: string;
    logoUrl?: string;
    coverImageUrl?: string;
    tagline?: string;
    primaryColor?: string;
    accentColor?: string;
    themePreset?: StoreThemePreset;
    searchTerm?: string;
    onSearchChange?: (value: string) => void;
    showHero?: boolean;
    headerActionLabel?: string;
    onHeaderAction?: () => void;
    headerActionDisabled?: boolean;
}

const presetBackgrounds: Record<StoreThemePreset, string> = {
    classic: 'radial-gradient(circle at top left, rgba(255,107,0,0.22), transparent 42%), linear-gradient(180deg, #111111 0%, #090909 100%)',
    urban: 'radial-gradient(circle at top right, rgba(255,107,0,0.18), transparent 38%), linear-gradient(135deg, #0f1115 0%, #0b0b0d 55%, #15110f 100%)',
    bistro: 'radial-gradient(circle at center top, rgba(255,178,107,0.20), transparent 42%), linear-gradient(180deg, #16100d 0%, #0b0908 100%)',
    fresh: 'radial-gradient(circle at top left, rgba(98,196,135,0.18), transparent 36%), linear-gradient(180deg, #0d1210 0%, #090909 100%)',
};

const StoreLayout: React.FC<StoreLayoutProps> = ({
    children,
    slug,
    establishmentName,
    logoUrl,
    coverImageUrl,
    tagline,
    primaryColor = '#ff6b00',
    accentColor = '#ffb26b',
    themePreset = 'classic',
    searchTerm = '',
    onSearchChange,
    showHero = true,
    headerActionLabel,
    onHeaderAction,
    headerActionDisabled = false,
}) => {
    const { itemCount, toggleCart } = useCartStore();
    const count = itemCount();
    const hasSearch = typeof onSearchChange === 'function';
    const resolvedCoverUrl = resolveAssetUrl(coverImageUrl);
    const resolvedLogoUrl = resolveAssetUrl(logoUrl);

    const wrapperStyle = {
        '--store-primary': primaryColor,
        '--store-accent': accentColor,
    } as CSSProperties;

    return (
        <div className="min-h-screen text-white" style={{ ...wrapperStyle, background: presetBackgrounds[themePreset] }}>
            <header className="sticky top-0 z-40 border-b border-white/10 bg-black/35 backdrop-blur-2xl">
                <div className="mx-auto flex h-16 max-w-5xl items-center justify-between gap-4 px-4">
                    <Link to={`/store/${slug}`} className="flex min-w-0 items-center gap-3">
                        {resolvedLogoUrl ? (
                            <div className="h-10 w-10 overflow-hidden rounded-2xl border border-white/10 bg-white/5 shadow-[0_0_22px_rgba(0,0,0,0.25)]">
                                <img
                                    src={resolvedLogoUrl}
                                    alt={establishmentName ? `Logo de ${establishmentName}` : 'Logo do restaurante'}
                                    className="h-full w-full object-cover"
                                />
                            </div>
                        ) : (
                            <div
                                className="flex h-10 w-10 items-center justify-center rounded-2xl text-white shadow-[0_0_20px_rgba(0,0,0,0.25)]"
                                style={{ background: 'linear-gradient(135deg, var(--store-primary), var(--store-accent))' }}
                            >
                                <ChefHat className="h-5 w-5" />
                            </div>
                        )}
                        <div className="min-w-0">
                            <div className="truncate text-sm font-black tracking-tight text-white">
                                {establishmentName ?? 'Garcom Digital'}
                            </div>
                            {tagline && (
                                <div className="hidden truncate text-xs text-white/55 sm:block">
                                    {tagline}
                                </div>
                            )}
                        </div>
                    </Link>

                    {hasSearch && (
                        <div className="hidden max-w-md flex-1 md:block">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/45" />
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => onSearchChange?.(e.target.value)}
                                    placeholder="Buscar no cardapio..."
                                    className="h-11 w-full rounded-2xl border border-white/10 bg-white/5 pl-9 pr-4 text-sm text-white outline-none transition placeholder:text-white/35 focus:border-white/20 focus:bg-white/8"
                                />
                            </div>
                        </div>
                    )}

                    <div className="flex items-center gap-2">
                        {headerActionLabel && onHeaderAction && (
                            <button
                                type="button"
                                onClick={onHeaderAction}
                                disabled={headerActionDisabled}
                                className="hidden rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-sm font-semibold text-white/85 transition hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-60 sm:inline-flex"
                            >
                                {headerActionLabel}
                            </button>
                        )}
                        <button
                            onClick={toggleCart}
                            className="relative rounded-2xl border border-white/10 bg-white/5 p-2.5 text-white/80 transition hover:bg-white/10 hover:text-white"
                        >
                            <ShoppingCart className="h-5 w-5" />
                            {count > 0 && (
                                <span
                                    className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px] font-black text-white"
                                    style={{ backgroundColor: 'var(--store-primary)' }}
                                >
                                    {count > 9 ? '9+' : count}
                                </span>
                            )}
                        </button>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-5xl px-4 pb-10 pt-5">
                {showHero && (
                    <section className="relative mb-6 overflow-hidden rounded-[28px] border border-white/10 bg-white/[0.04] shadow-[0_30px_90px_rgba(0,0,0,0.28)] md:mb-8 md:rounded-[32px]">
                        {resolvedCoverUrl ? (
                            <div className="absolute inset-0">
                                <img
                                    src={resolvedCoverUrl}
                                    alt=""
                                    className="h-full w-full object-cover opacity-30"
                                />
                                <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(8,8,8,0.18),rgba(8,8,8,0.92))]" />
                            </div>
                        ) : (
                            <div
                                className="absolute inset-0 opacity-80"
                                style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01)), radial-gradient(circle at top left, color-mix(in srgb, var(--store-primary) 28%, transparent), transparent 42%)' }}
                            />
                        )}

                        <div className="relative grid gap-5 px-4 py-5 md:grid-cols-[1.5fr_0.9fr] md:gap-6 md:px-7 md:py-8">
                            <div className="flex min-w-0 items-start gap-4">
                                {resolvedLogoUrl ? (
                                    <div className="h-20 w-20 shrink-0 overflow-hidden rounded-[24px] border border-white/10 bg-black/25 shadow-[0_20px_40px_rgba(0,0,0,0.22)]">
                                        <img src={resolvedLogoUrl} alt="" className="h-full w-full object-cover" />
                                    </div>
                                ) : (
                                    <div
                                        className="flex h-20 w-20 shrink-0 items-center justify-center rounded-[24px] text-white shadow-[0_20px_40px_rgba(0,0,0,0.22)]"
                                        style={{ background: 'linear-gradient(135deg, var(--store-primary), var(--store-accent))' }}
                                    >
                                        <ChefHat className="h-9 w-9" />
                                    </div>
                                )}

                                <div className="min-w-0">
                                    <div
                                        className="inline-flex rounded-full border px-3 py-1 text-[11px] font-black uppercase tracking-[0.28em]"
                                        style={{
                                            borderColor: 'color-mix(in srgb, var(--store-primary) 36%, transparent)',
                                            backgroundColor: 'color-mix(in srgb, var(--store-primary) 14%, transparent)',
                                            color: 'var(--store-accent)',
                                        }}
                                    >
                                        Cardapio online
                                    </div>
                                    <h1 className="mt-3 max-w-2xl text-[2rem] font-black leading-[1.02] text-white md:mt-4 md:text-5xl">
                                        {establishmentName ?? 'Sua proxima refeicao com identidade propria'}
                                    </h1>
                                    <p className="mt-3 max-w-2xl text-sm leading-6 text-white/72 md:text-base">
                                        {tagline?.trim() || 'Escolha seus itens favoritos, faca o pedido pelo site e acompanhe tudo com a cara do restaurante.'}
                                    </p>
                                    <div className="mt-4 flex flex-wrap gap-2 md:hidden">
                                        {['Pedido rapido', 'Checkout no site', 'Acompanhe online'].map((item) => (
                                            <span
                                                key={item}
                                                className="rounded-full border px-3 py-1 text-[11px] font-semibold text-white/80"
                                                style={{
                                                    borderColor: 'color-mix(in srgb, var(--store-primary) 28%, transparent)',
                                                    backgroundColor: 'color-mix(in srgb, var(--store-primary) 10%, transparent)',
                                                }}
                                            >
                                                {item}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="hidden gap-3 self-end sm:grid-cols-3 md:grid md:grid-cols-1">
                                {[
                                    { label: 'Pedido rapido', value: 'Checkout direto no site' },
                                    { label: 'Visual proprio', value: 'Loja com cores e capa da casa' },
                                    { label: 'Acompanhamento', value: 'Cliente acompanha o pedido online' },
                                ].map((item) => (
                                    <div key={item.label} className="rounded-2xl border border-white/10 bg-black/25 p-4 backdrop-blur-md">
                                        <div className="text-[11px] font-black uppercase tracking-[0.24em] text-white/45">{item.label}</div>
                                        <div className="mt-2 text-sm font-semibold text-white">{item.value}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {hasSearch && (
                    <div className="mb-6 md:hidden">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/45" />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => onSearchChange?.(e.target.value)}
                                placeholder="Buscar no cardapio..."
                                className="h-12 w-full rounded-2xl border border-white/10 bg-white/5 pl-9 pr-4 text-sm text-white outline-none transition placeholder:text-white/35 focus:border-white/20 focus:bg-white/8"
                            />
                        </div>
                    </div>
                )}

                <div className="animate-fade-in">
                    {children}
                </div>
            </main>

            <footer className="border-t border-white/10 py-8">
                <div className="mx-auto flex max-w-5xl flex-col items-center justify-between gap-3 px-4 text-center text-sm text-white/55 md:flex-row md:text-left">
                    <div>
                        <div className="font-semibold text-white/82">{establishmentName ?? 'Garcom Digital'}</div>
                        <div className="mt-1 text-xs">{tagline?.trim() || 'Loja publica com identidade visual propria.'}</div>
                    </div>
                    <div>
                        Powered by <span style={{ color: 'var(--store-primary)' }} className="font-black">Garcom Digital</span>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default StoreLayout;
